import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { supabase } from '@/lib/supabase'
import type { VehicleFeature } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

import {
  vehicleFeatureSchema,
  COMMON_FEATURES,
  type VehicleFeatureFormData,
} from '@/schemas/vehicle'

interface VehicleFeatureDialogProps {
  featureId: string | null
  vehicleId: string
  accountId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function VehicleFeatureDialog({
  featureId,
  vehicleId,
  accountId,
  open,
  onOpenChange,
}: VehicleFeatureDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const isEditing = !!featureId

  const { data: feature, isLoading: isLoadingFeature } = useQuery({
    queryKey: ['vehicle-feature', featureId],
    queryFn: async () => {
      if (!featureId) return null
      const { data, error } = await supabase
        .from('vehicle_features')
        .select('*')
        .eq('id', featureId)
        .single()
      if (error) throw error
      return data as VehicleFeature
    },
    enabled: !!featureId && open,
  })

  const form = useForm<VehicleFeatureFormData>({
    resolver: zodResolver(vehicleFeatureSchema),
    defaultValues: {
      name: '',
      icon: '',
    },
  })

  useEffect(() => {
    if (feature) {
      form.reset({
        name: feature.name,
        icon: feature.icon || '',
      })
    } else if (!isEditing && open) {
      form.reset({
        name: '',
        icon: '',
      })
    }
  }, [feature, form, isEditing, open])

  const createMutation = useMutation({
    mutationFn: async (data: VehicleFeatureFormData) => {
      const { error } = await supabase.from('vehicle_features').insert({
        ...data,
        vehicle_id: vehicleId,
        account_id: accountId,
        icon: data.icon || null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      toast({ title: 'Feature added successfully' })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: VehicleFeatureFormData) => {
      if (!featureId) throw new Error('Feature ID is required')
      const { error } = await supabase
        .from('vehicle_features')
        .update({
          ...data,
          icon: data.icon || null,
        })
        .eq('id', featureId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle-feature', featureId] })
      toast({ title: 'Feature updated successfully' })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  const onSubmit = (data: VehicleFeatureFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
    }
    onOpenChange(open)
  }

  const handleQuickAdd = (feature: (typeof COMMON_FEATURES)[number]) => {
    form.setValue('name', feature.name)
    form.setValue('icon', feature.icon)
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Feature' : 'Add Feature'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the feature information.'
              : 'Add a new feature to this vehicle.'}
          </DialogDescription>
        </DialogHeader>

        {isEditing && isLoadingFeature ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {!isEditing && (
                <div className="space-y-2">
                  <Label>Quick Add</Label>
                  <div className="flex flex-wrap gap-2">
                    {COMMON_FEATURES.slice(0, 6).map((f) => (
                      <Button
                        key={f.name}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAdd(f)}
                      >
                        {f.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Feature Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Leather Interior" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="icon"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Icon (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="armchair"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormDescription>
                      Lucide icon name (e.g., wifi, tv, thermometer)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isEditing ? 'Update Feature' : 'Add Feature'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
